/* eslint-disable import/extensions -- no worries here */
import { fetchTranslations } from '@ori/i18n';
import i18next from 'i18next';
import type { FC, PropsWithChildren } from 'react';
import { useEffect, useMemo } from 'react';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import useSWRImmutable from 'swr/immutable';

import type { CombinedTranslations } from '../models';
import { CombinedTranslationKeys } from '../models';
import { useAppConfigurationContext } from '~/contexts/AppConfigurationContext';
import { log } from '~/utils';

const DEFAULT_LANGUAGE_KEY = 'default';
export const DEFAULT_NAMESPACE = 'translations';

const i18nextInstance = i18next
  .createInstance({
    keySeparator: '::',
    lng: DEFAULT_LANGUAGE_KEY,
    fallbackLng: DEFAULT_LANGUAGE_KEY,
    defaultNS: DEFAULT_NAMESPACE,
    resources: {},

    interpolation: {
      escapeValue: false, // react already safes from xss
      prefix: '{',
      suffix: '}',
    },

    react: {
      bindI18n: 'added removed languageChanged',
      bindI18nStore: 'added removed',
    },
  })
  .use(initReactI18next);

i18nextInstance.init();

export const I18nContainer: FC<
  PropsWithChildren<{ preloadedTranslations?: CombinedTranslations }>
> = ({ children, preloadedTranslations }) => {
  const { currentLanguage = DEFAULT_LANGUAGE_KEY, onlineBaseUrl } = useAppConfigurationContext();

  if (!onlineBaseUrl && typeof window === 'undefined' && !preloadedTranslations) {
    log.warn('cannot download translations on server if onlineBaseUrl is not provided');
  }

  const payload = useMemo(
    () =>
      !preloadedTranslations && (!!onlineBaseUrl || typeof window !== 'undefined')
        ? {
            baseUrl: onlineBaseUrl ?? window.location.origin,
            currentLanguage,
            keys: CombinedTranslationKeys,
          }
        : null,
    [currentLanguage, onlineBaseUrl, preloadedTranslations],
  );
  const { data: localizationData } = useSWRImmutable(
    // Either include onlineBaseUrl to enable ssr or fallback on client fetch only
    payload,
    fetchTranslations<CombinedTranslations>,
    { suspense: true },
  );

  useEffect(() => {
    const newResources: CombinedTranslations | undefined =
      localizationData?.translations ?? preloadedTranslations;

    i18nextInstance.addResourceBundle(
      currentLanguage,
      DEFAULT_NAMESPACE,
      newResources ?? {},
      false,
      true,
    );
  }, [localizationData, currentLanguage, preloadedTranslations]);

  useEffect(() => {
    if (currentLanguage) {
      i18nextInstance.changeLanguage(currentLanguage);
    }
  }, [currentLanguage]);

  return <I18nextProvider i18n={i18nextInstance}>{children}</I18nextProvider>;
};
